import React from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useBracketLeaderboard, useMyBracketStats } from '@/features/predictor/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { formatNumber, latinNumerals } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

const PODIUM = ['#F4C84B', '#C7CDDA', '#CD8B53'];

export default function PredictorLeaderboardScreen() {
  const { t } = useTranslation();
  const { userId } = useAuth();
  const { locale } = useLocale();
  const nums = latinNumerals(locale);
  const board = useBracketLeaderboard();
  const myStats = useMyBracketStats();
  const rows = board.data ?? [];

  return (
    <Screen>
      <BackHeader title={t('predictor.leaderboard')} />
      <ScrollView contentContainerStyle={{ paddingBottom: 40 }}>
        {myStats.data ? (
          <Card className="mx-4 mb-4 flex-row items-center justify-around px-4 py-4">
            <View className="items-center">
              <AppText variant="title" weight="bold" tabular className="text-volt-bright">
                {formatNumber(myStats.data.points, nums)}
              </AppText>
              <AppText variant="micro" className="uppercase tracking-wide text-muted">
                {t('predictor.points')}
              </AppText>
            </View>
            <View className="items-center">
              <AppText variant="title" weight="bold" tabular className="text-ink">
                #{formatNumber(myStats.data.rank, nums)}
              </AppText>
              <AppText variant="micro" className="uppercase tracking-wide text-muted">
                {t('predictor.rank')}
              </AppText>
            </View>
          </Card>
        ) : null}

        {board.isPending ? (
          <View className="gap-3 px-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </View>
        ) : board.isError ? (
          <ErrorState error={board.error} onRetry={() => board.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="podium-outline"
            title={t('predictor.emptyBoardTitle')}
            body={t('predictor.emptyBoardBody')}
          />
        ) : (
          <Card className="mx-4 overflow-hidden">
            {rows.map((entry, index) => {
              const isMine = entry.user_id === userId;
              const medal = entry.rank <= 3 ? PODIUM[entry.rank - 1] : null;
              return (
                <View
                  key={entry.user_id}
                  className={`flex-row items-center gap-3 px-4 py-3 ${index > 0 ? 'border-t border-line' : ''} ${
                    isMine ? 'bg-volt/10' : ''
                  }`}
                >
                  {medal ? (
                    <View
                      className="h-7 w-7 items-center justify-center rounded-full"
                      style={{ backgroundColor: medal }}
                    >
                      <AppText weight="bold" tabular style={{ color: '#1A1408', fontSize: 12 }}>
                        {formatNumber(entry.rank, nums)}
                      </AppText>
                    </View>
                  ) : (
                    <AppText variant="caption" weight="bold" tabular className="w-7 text-center text-muted">
                      {formatNumber(entry.rank, nums)}
                    </AppText>
                  )}
                  <PlayerAvatar uri={entry.avatar_url} name={entry.display_name} size={34} />
                  <AppText variant="caption" weight="bold" className="flex-1 text-ink" numberOfLines={1}>
                    {entry.display_name}
                    {isMine ? `  ·  ${t('fantasy.leaderboards.you')}` : ''}
                  </AppText>
                  <AppText variant="body" weight="bold" tabular className="text-volt-bright">
                    {formatNumber(entry.points, nums)}
                  </AppText>
                </View>
              );
            })}
          </Card>
        )}
      </ScrollView>
    </Screen>
  );
}
